/**
 * opfs-store.ts
 * =============
 * OPFS (Origin Private File System) persistence for columnar candle datasets.
 *
 * Layout:
 *   datasets/<contentHash>/manifest.json  — DatasetManifest JSON
 *   datasets/<contentHash>/base.col       — serialized base CandleColumns
 *   datasets/<contentHash>/<tfMin>.col    — serialized tier CandleColumns
 *
 * Worker writes use createWritable() (async, works in workers).
 * Main-thread reads use getFile().arrayBuffer() (async).
 *
 * When OPFS is unavailable (old browsers), callers fall back to Dexie.
 */

import { deserializeColumns, type CandleColumns } from "@/lib/lytick/columnar";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DatasetManifest {
  contentHash: string;
  symbol: string;
  granularitySeconds: number;
  rowCount: number;
  firstTs: number;
  lastTs: number;
  savedAt: number;
  tierTfMinutes: number[];
}

// ─── Feature detection ────────────────────────────────────────────────────────

export function isOpfsAvailable(): boolean {
  return (
    typeof navigator !== "undefined" &&
    "storage" in navigator &&
    typeof navigator.storage.getDirectory === "function"
  );
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

async function getDatasetDir(
  contentHash: string,
  create: boolean,
): Promise<FileSystemDirectoryHandle | null> {
  try {
    const root = await navigator.storage.getDirectory();
    const datasets = await root.getDirectoryHandle("datasets", { create });
    return await datasets.getDirectoryHandle(contentHash, { create });
  } catch {
    return null;
  }
}

async function writeFile(
  dir: FileSystemDirectoryHandle,
  name: string,
  data: ArrayBuffer | string,
): Promise<void> {
  const handle = await dir.getFileHandle(name, { create: true });
  const writable = await handle.createWritable();
  await writable.write(data instanceof ArrayBuffer ? data : new TextEncoder().encode(data));
  await writable.close();
}

async function readFileBuffer(
  dir: FileSystemDirectoryHandle,
  name: string,
): Promise<ArrayBuffer | null> {
  try {
    const handle = await dir.getFileHandle(name);
    const file = await handle.getFile();
    return await file.arrayBuffer();
  } catch {
    return null;
  }
}

// ─── Write ────────────────────────────────────────────────────────────────────

/**
 * Persist a dataset to OPFS. Called from the CSV parser worker after ingest.
 * Writes manifest + base + all tier buffers.
 */
export async function writeDatasetToOpfs(
  manifest: DatasetManifest,
  baseBuffer: ArrayBuffer,
  tierBuffers: { tfMinutes: number; buffer: ArrayBuffer }[],
): Promise<void> {
  const dir = await getDatasetDir(manifest.contentHash, true);
  if (!dir) throw new Error("OPFS: could not create dataset directory");

  await writeFile(dir, "manifest.json", JSON.stringify(manifest));
  await writeFile(dir, "base.col", baseBuffer);

  for (const { tfMinutes, buffer } of tierBuffers) {
    await writeFile(dir, `${tfMinutes}.col`, buffer);
  }
}

// ─── Read ─────────────────────────────────────────────────────────────────────

/** Check if a dataset is persisted in OPFS. */
export async function hasDatasetInOpfs(contentHash: string): Promise<boolean> {
  const dir = await getDatasetDir(contentHash, false);
  if (!dir) return false;
  const buf = await readFileBuffer(dir, "manifest.json");
  return buf !== null;
}

/** Read the manifest for a stored dataset. */
export async function readManifestFromOpfs(
  contentHash: string,
): Promise<DatasetManifest | null> {
  const dir = await getDatasetDir(contentHash, false);
  if (!dir) return null;
  const buf = await readFileBuffer(dir, "manifest.json");
  if (!buf) return null;
  try {
    return JSON.parse(new TextDecoder().decode(buf)) as DatasetManifest;
  } catch {
    return null;
  }
}

/** Read and deserialize the base columns from OPFS. */
export async function readBaseColumnsFromOpfs(
  contentHash: string,
): Promise<CandleColumns | null> {
  const dir = await getDatasetDir(contentHash, false);
  if (!dir) return null;
  const buf = await readFileBuffer(dir, "base.col");
  if (!buf) return null;
  try {
    return deserializeColumns(buf);
  } catch {
    return null;
  }
}

/** Read and deserialize all tier columns from OPFS. */
export async function readTierColumnsFromOpfs(
  contentHash: string,
  tierTfMinutes: number[],
): Promise<Map<number, CandleColumns>> {
  const tiers = new Map<number, CandleColumns>();
  const dir = await getDatasetDir(contentHash, false);
  if (!dir) return tiers;

  for (const tfMinutes of tierTfMinutes) {
    const buf = await readFileBuffer(dir, `${tfMinutes}.col`);
    if (buf) {
      try {
        tiers.set(tfMinutes, deserializeColumns(buf));
      } catch {
        // Skip corrupt tier; will fall back to on-demand aggregation
      }
    }
  }

  return tiers;
}

/** Read base + all tiers from OPFS. Convenience wrapper used on restore. */
export async function readFullDatasetFromOpfs(contentHash: string): Promise<{
  manifest: DatasetManifest;
  base: CandleColumns;
  tiers: Map<number, CandleColumns>;
} | null> {
  const manifest = await readManifestFromOpfs(contentHash);
  if (!manifest) return null;

  const base = await readBaseColumnsFromOpfs(contentHash);
  if (!base) return null;

  const tiers = await readTierColumnsFromOpfs(contentHash, manifest.tierTfMinutes);

  return { manifest, base, tiers };
}

/** Delete a stored dataset from OPFS. */
export async function deleteDatasetFromOpfs(contentHash: string): Promise<void> {
  try {
    const root = await navigator.storage.getDirectory();
    const datasets = await root.getDirectoryHandle("datasets", { create: false });
    await datasets.removeEntry(contentHash, { recursive: true });
  } catch {
    // Best effort
  }
}
